import { CssBaseline, createTheme, ThemeProvider } from "@material-ui/core";
import {
  EPOCUS_BG,
  EPOCUS_BLUE,
  EPOCUS_FONT_FAMILY,
  EPOCUS_GREEN,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_ORANGE,
  EPOCUS_PRIMARY,
  EPOCUS_PRIMARY_HOVER_BG,
  EPOCUS_PURPLE,
  EPOCUS_RADIUS_BUTTON,
  EPOCUS_RED,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
} from "./epocus-palette";

// ============================================================================
// ⚠️  MOBILE-FIRST STYLING — READ BEFORE EDITING
// ============================================================================
// ePOCUS is a mobile-first application. All base styles MUST target the
// smallest supported viewport (375px, iPhone SE). Larger viewports are handled
// via progressive enhancement using min-width breakpoints only:
//
//   ✅  [epocusTheme.breakpoints.up("sm")]: { ... }  → tablet (768px+)
//   ✅  [epocusTheme.breakpoints.up("md")]: { ... }  → large tablet (1024px+)
//   ❌  @media (max-width: ...)  → NEVER use max-width breakpoints
//
// Breakpoint map:
//   xs =    0  → phone (base styles, no media query needed)
//   sm =  768  → tablet portrait
//   md = 1024  → large tablet / tablet landscape
//   lg = 1280  → (unused, reserved)
//   xl = 1920  → (unused, reserved)
//
// This applies to every styled component in the /epocus route tree — not just
// this file. When in doubt: if it looks right on a small phone, start there.
// ============================================================================

// Palette stub — pink and disabled are required by Elephant's MUI type augmentation
// (src/theme.d.ts) but have no ePOCUS equivalent. They are not used or referenced
// anywhere in the ePOCUS route tree.
const UNUSED_PALETTE_STUB = { main: EPOCUS_ICON_ACTIVE } as const;

export const epocusTheme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 768, md: 1024, lg: 1280, xl: 1920 },
  },
  palette: {
    primary: { main: EPOCUS_PRIMARY, contrastText: EPOCUS_WHITE },
    background: { default: EPOCUS_BG, paper: EPOCUS_WHITE },
    text: { primary: EPOCUS_TEXT_PRIMARY, secondary: EPOCUS_TEXT_SECONDARY },
    brand: { main: EPOCUS_ICON_ACTIVE },
    blue: { main: EPOCUS_BLUE },
    purple: { main: EPOCUS_PURPLE },
    pink: UNUSED_PALETTE_STUB,
    black: { main: EPOCUS_TEXT_PRIMARY },
    disabled: UNUSED_PALETTE_STUB,
    red: { main: EPOCUS_RED },
    yellow: { main: EPOCUS_ORANGE },
    orange: { main: EPOCUS_ORANGE },
    green: { main: EPOCUS_GREEN },
  },
  typography: {
    fontFamily: EPOCUS_FONT_FAMILY,
  },
  shape: {
    borderRadius: EPOCUS_RADIUS_BUTTON,
  },
  overrides: {
    MuiButton: {
      outlinedPrimary: {
        backgroundColor: EPOCUS_WHITE,
        "&:hover": { backgroundColor: EPOCUS_PRIMARY_HOVER_BG },
      },
    },
    MuiCssBaseline: {
      // Undo Elephant's html { font-size: 62.5% } so 1rem = 16px naturally.
      // Scoped to ePOCUS via JSS lifecycle — removed when this theme unmounts.
      "@global": {
        html: { fontSize: "100%" },
      },
    },
    MuiInputLabel: {
      // Prevent long labels from overflowing narrow inputs on mobile.
      // 28px = 14px left padding × 2 sides in outlined variant.
      outlined: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "calc(100% - 28px)",
      },
    },
  },
});

export const EpocusThemeProvider: FCwC = ({ children }) => (
  <ThemeProvider theme={epocusTheme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);
